import React, { useState } from "react";
import { Mail, Phone, MapPin, Send, Clock } from "lucide-react";
import toast from "react-hot-toast";
import { contactService } from "../services/contact";
import { optimizeImageUrl } from "../utils/images";

const CONTACT_INFO = [
  { Icon: Mail, label: "Email", value: "adebiyiaderinsola6@gmail.com", href: "mailto:adebiyiaderinsola6@gmail.com" },
  { Icon: Phone, label: "Phone", value: "+234 912 812 9749", href: "tel:+2349128129749" },
  { Icon: MapPin, label: "Location", value: "Ibadan, Oyo State, Nigeria", href: null },
];

const HOURS = [
  { day: "Monday - Friday", hours: "9:00 AM - 6:00 PM" },
  { day: "Saturday", hours: "10:00 AM - 4:00 PM" },
  { day: "Sunday", hours: "Closed" },
];

const HERO_IMAGE = optimizeImageUrl(
  "https://res.cloudinary.com/dirgb6nie/image/upload/v1775254817/e42d39e0-5f7a-4201-bad7-bc1e7e5b1054_yd1ycl.jpg",
  { width: 1600 }
);

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
    </svg>
  );
}

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (field) => (event) => setForm((previous) => ({ ...previous, [field]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();

    setSending(true);

    try {
      const response = await contactService.sendMessage({
        name: form.name,
        email: form.email,
        subject: form.subject,
        message: form.message,
      });

      toast.success(response?.message || "Message sent successfully. We'll be in touch soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      toast.error(error.message || "Message failed to send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="relative bg-brand-dark pt-36 pb-28 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-15" style={{ backgroundImage: `url('${HERO_IMAGE}')` }} />
        <div className="max-w-7xl mx-auto">
          <span className="section-label text-brand-red">Get in Touch</span>
          <h1 className="font-display text-5xl sm:text-6xl text-white font-light mt-3">Contact Us</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <h2 className="font-display text-3xl text-brand-dark mb-4">We'd Love to Hear From You</h2>
            <p className="font-body text-gray-400 leading-relaxed mb-10 max-w-md">
              Whether you have a question about a fragrance, your order, or just want to say hello - our team is ready to help.
            </p>
            <div className="flex flex-col gap-6 mb-12">
              {CONTACT_INFO.map(({ Icon, label, value, href }) => (
                <div key={label} className="flex items-start gap-4 group">
                  <div className="w-11 h-11 rounded-sm bg-brand-cream flex items-center justify-center shrink-0 group-hover:bg-brand-red transition-colors duration-300">
                    <Icon size={17} className="text-brand-red group-hover:text-white transition-colors duration-300" />
                  </div>
                  <div>
                    <p className="font-body text-[10px] uppercase tracking-widest text-gray-400 mb-0.5">{label}</p>
                    {href
                      ? <a href={href} className="font-body text-brand-charcoal hover:text-brand-red transition-colors">{value}</a>
                      : <p className="font-body text-brand-charcoal">{value}</p>}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-6 bg-brand-cream rounded-sm">
              <div className="flex items-center gap-2 mb-4">
                <Clock size={15} className="text-brand-red" />
                <h3 className="font-display text-xl text-brand-dark">Business Hours</h3>
              </div>
              {HOURS.map(({ day, hours }) => (
                <div key={day} className="flex justify-between py-2.5 border-b border-gray-200 last:border-0">
                  <span className="font-body text-sm text-gray-500">{day}</span>
                  <span className="font-body text-sm font-semibold text-brand-charcoal">{hours}</span>
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="bg-brand-cream p-8 rounded-sm">
            <h2 className="font-display text-2xl text-brand-dark mb-6">Send a Message</h2>
            <div className="grid sm:grid-cols-2 gap-5 mb-5">
              <div>
                <label className="block font-body text-[10px] uppercase tracking-widest text-gray-400 mb-2">Full Name <span className="text-brand-red">*</span></label>
                <input type="text" value={form.name} onChange={set("name")} placeholder="Your name" required className="input-field" />
              </div>
              <div>
                <label className="block font-body text-[10px] uppercase tracking-widest text-gray-400 mb-2">Email <span className="text-brand-red">*</span></label>
                <input type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" required className="input-field" />
              </div>
            </div>
            <div className="mb-5">
              <label className="block font-body text-[10px] uppercase tracking-widest text-gray-400 mb-2">Subject <span className="text-brand-red">*</span></label>
              <input type="text" value={form.subject} onChange={set("subject")} placeholder="What's this about?" required className="input-field" />
            </div>
            <div className="mb-6">
              <label className="block font-body text-[10px] uppercase tracking-widest text-gray-400 mb-2">Message <span className="text-brand-red">*</span></label>
              <textarea value={form.message} onChange={set("message")} placeholder="Tell us more..." rows={5} required className="input-field resize-none" />
            </div>
            <button type="submit" disabled={sending} className="w-full flex items-center justify-center gap-2 py-4 bg-brand-red text-white font-body font-medium text-sm tracking-wide rounded-sm hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {sending ? <><Spinner />Sending...</> : <><Send size={14} />Send Message</>}
            </button>
            <p className="mt-3 font-body text-xs text-gray-500 leading-relaxed">
              Your message is delivered directly through our contact inbox, and we will reply to the email you provide.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
